import React, { useState, useEffect, useRef } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { ErrorBoundary } from 'react-error-boundary';
import { Team } from '@/types';
import { supabase } from '@/lib/supabaseClient';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, ClipboardList, Users as UsersIcon, Loader2, Settings as SettingsIcon, Zap, Bell, User, Activity, Brain, Palette } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useUsers } from "@/components/settings/hooks/useUsers";
import { useTeams } from "@/components/settings/hooks/useTeams";
import { useInstallers } from "@/components/settings/hooks/useInstallers";
import { PipelinesTab } from "@/components/pipeline/PipelinesTab";
import { TemplatesTab } from "@/components/settings/TemplatesTab";
import { UsersManagementTab } from "@/components/settings/components/UsersManagementTab";
import { AutomationManagement } from "@/components/settings/components/AutomationManagement";
import { NotificationTemplates } from "@/components/NotificationTemplates";
import { LogsTab } from "@/components/settings/components/LogsTab";
import { KnowledgeBaseSettings } from "@/components/settings/components/KnowledgeBaseSettings";
import { ThemeTab } from "@/components/settings/ThemeTab";
import { EmailDomainConfiguration } from "@/components/settings/components/notifications/EmailDomainConfiguration";
import { EmailSignatureSettings } from "@/components/settings/components/notifications/EmailSignatureSettings";
import { useLocation, useNavigate } from 'react-router-dom';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [isMounted, setIsMounted] = useState(false);
  const hasAccessedUsersTab = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { toast } = useToast();
  const isFetchingTeams = useRef(false);
  const isFetchingDeletedUsers = useRef(false);

  const {
    users = [],
    deletedUsers = [],
    loading: usersLoading = false,
    deletedUsersLoading = false,
    roles = [],
    managers = [],
    teams: userTeams = [],
    createUser,
    updateUser,
    deleteUser,
    fetchDeletedUsers,
    fetchUsers,
    fetchTeams: fetchUserTeams,
    addUserError,
  } = useUsers();

  const {
    teams: allTeams = [],
    isLoading: teamsLoading = false,
    fetchTeams,
    createTeam,
    updateTeam,
    deleteTeam: deleteTeamWithResult,
  } = useTeams();

  const {
    installers = [],
    isLoading: installersLoading = false,
    createInstaller,
    updateInstaller,
    deleteInstaller: deleteInstallerWithResult,
  } = useInstallers();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['users', 'pipelines', 'templates', 'notifications', 'webhooks', 'logs', 'theme', 'knowledge'];
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);


  const handleRefreshData = async () => {
    try {
      const { data, error } = await supabase
        .from('stages')
        .select('*')
        .order('stage_order');
      if (error) {
        if (error.code === '42P01') {
          toast({
            title: "Error",
            description: "Stages table not found. Using default stages.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh stages.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      // Fetch teams if needed
      if (allTeams.length === 0 && !isFetchingTeams.current) {
        isFetchingTeams.current = true;
        fetchTeams().finally(() => {
          isFetchingTeams.current = false;
        });
      }
      // Fetch deleted users only once when first accessing users tab
      if (!hasAccessedUsersTab.current && !isFetchingDeletedUsers.current) {
        hasAccessedUsersTab.current = true;
        isFetchingDeletedUsers.current = true;
        fetchDeletedUsers().finally(() => {
          isFetchingDeletedUsers.current = false;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, allTeams.length]);

  const handleCreateTeam = async (teamData: Omit<Team, 'id'>): Promise<void> => {
    await createTeam(teamData);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team creation:', error);
    }
  };

  const handleDeleteTeam = async (teamId: string): Promise<void> => {
    await deleteTeamWithResult(teamId);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team deletion:', error);
    }
  };

  const handleUpdateTeam = async (teamId: string, updates: Partial<Team>): Promise<void> => {
    // Find the existing team to merge with updates
    const existingTeam = allTeams.find(t => t.id === teamId);
    if (!existingTeam) {
      throw new Error('Team not found');
    }
    
    // Merge existing team data with updates
    const updatedTeam = { ...existingTeam, ...updates };
    await updateTeam(updatedTeam);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team update:', error);
    }
  };

  const handleDeleteInstaller = async (installerId: string): Promise<void> => {
    await deleteInstallerWithResult(installerId);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/app/settings?tab=${value}`, { replace: true });
  };

  const isLoading = usersLoading || teamsLoading || installersLoading;

  const canManageUsers = true;
  const canManageTeams = true;
  const canManageInstallers = true;
  const canManageUsersTab = canManageUsers || canManageTeams || canManageInstallers;
  const canManagePipelines = true;
  const canManageChecklists = true;
  const canManageWebhooks = true;
  const canViewLogs = true;
  const canManageKnowledge = true; // Admin-only access for now

  const ErrorFallback = ({ error }: { error: Error }) => (
    <div className="p-4 text-destructive">
      Error loading settings tab: {error.message}. Check component exports in settings/components/*.
    </div>
  );

  return (
    <div className="container mx-auto py-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={handleTabChange}
        className="space-y-4"
      >
        <TabsList className="flex w-full gap-0 rounded-lg shadow-sm border border-muted bg-transparent p-0">
          
          {canManageUsersTab && (
            <TabsTrigger
              value="users"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <UsersIcon className="w-4 h-4 mr-1" />
              Users
            </TabsTrigger>
          )}
          {canManagePipelines && (
            <TabsTrigger
              value="pipelines"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <BarChart3 className="w-4 h-4 mr-1" />
              Pipelines
            </TabsTrigger>
          )}

          {canManageChecklists && (
            <TabsTrigger
              value="templates"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <ClipboardList className="w-4 h-4 mr-1" />
              Templates
            </TabsTrigger>
          )}
          {canManageWebhooks && (
            <TabsTrigger
              value="notifications"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <Bell className="w-4 h-4 mr-1" />
              Notifications
            </TabsTrigger>
          )}
          {canManageWebhooks && (
            <TabsTrigger
              value="webhooks"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <Zap className="w-4 h-4 mr-1" />
              Automation
            </TabsTrigger>
          )}
          {canManageWebhooks && (
            <TabsTrigger
              value="theme"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none rounded-r-lg"
            >
              <Palette className="w-4 h-4 mr-1" />
              Theme
            </TabsTrigger>
          )}
          {canViewLogs && (
            <TabsTrigger
              value="logs"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              <Activity className="w-4 h-4 mr-1" />
              Logs
            </TabsTrigger>
          )}
          {canManageKnowledge && (
            <TabsTrigger
              value="knowledge"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none rounded-r-lg"
            >
              <Brain className="w-4 h-4 mr-1" />
              AI
            </TabsTrigger>
          )}
        </TabsList>

        <div className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <TabsContent value="users" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Users tab.</div>}>
                  <UsersManagementTab
                    // Users props
                    users={users}
                    deletedUsers={deletedUsers}
                    usersLoading={usersLoading}
                    deletedUsersLoading={deletedUsersLoading}
                    roles={roles}
                    teams={userTeams}
                    managers={managers}
                    createUser={createUser}
                    updateUser={updateUser}
                    deleteUser={deleteUser}
                    fetchDeletedUsers={() => {
                      // Reset the guard and fetch deleted users
                      isFetchingDeletedUsers.current = false;
                      fetchDeletedUsers();
                    }}
                    fetchUsers={fetchUsers}
                    addUserError={addUserError}
                    onEmailChange={async (email: string) => {
                      try {
                        const { data, error } = await supabase
                          .from('users')
                          .select('id')
                          .eq('email', email)
                          .maybeSingle();
                        if (data && data.id) {
                          return { exists: true };
                        }
                        if (error) {
                          throw new Error('Error checking email uniqueness');
                        }
                        return { exists: false };
                      } catch (error) {
                        console.error('Error checking email:', error);
                        return { exists: false };
                      }
                    }}
                    onEmailCheck={async (email: string) => {
                      try {
                        const { data, error } = await supabase
                          .from('users')
                          .select('id')
                          .eq('email', email)
                          .maybeSingle();
                        if (data && data.id) {
                          return true;
                        }
                        if (error) {
                          throw new Error('Error checking email uniqueness');
                        }
                        return false;
                      } catch (error) {
                        console.error('Error checking email:', error);
                        return false;
                      }
                    }}
                    currentUserId={undefined}
                    // Teams props
                    allTeams={allTeams}
                    teamsLoading={teamsLoading}
                    fetchTeams={fetchTeams}
                    createTeam={handleCreateTeam}
                    updateTeam={handleUpdateTeam}
                    deleteTeam={handleDeleteTeam}
                    // Installers props
                    installers={installers}
                    installersLoading={installersLoading}
                    createInstaller={createInstaller}
                    updateInstaller={updateInstaller}
                    deleteInstaller={handleDeleteInstaller}
                    // Permissions
                    canManageUsers={canManageUsers}
                    canManageTeams={canManageTeams}
                    canManageInstallers={canManageInstallers}
                  />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="pipelines" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Pipelines tab.</div>}>
                  <PipelinesTab />
                </ErrorBoundary>
              </TabsContent>



              <TabsContent value="templates" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Templates tab.</div>}>
                  <TemplatesTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-4">
                {/* Email Configuration - shown for all tenants except Solerro */}
                <EmailDomainConfiguration />

                {/* Email Signature Settings */}
                <EmailSignatureSettings />

                {/* Notification Templates */}
                <div>
                  <p className="text-muted-foreground mb-4">
                    Customize notification content and templates for different notification types.
                  </p>
                  <NotificationTemplates />
                </div>
              </TabsContent>

              <TabsContent value="webhooks" className="space-y-4">
                <AutomationManagement />
              </TabsContent>

              <TabsContent value="theme" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Theme tab.</div>}>
                  <ThemeTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="logs" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Logs tab.</div>}>
                  <LogsTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="knowledge" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-red-500">Error loading AI tab.</div>}>
                  <KnowledgeBaseSettings />
                </ErrorBoundary>
              </TabsContent>
            </ErrorBoundary>
          )}
        </div>
      </Tabs>
      

    </div>
  );
};

export default Settings;